import { KeyedArray } from "@repo/models";
import { Button, ButtonTypes, Column, GridPreLoaded, LocalDateColumn } from "@repo/ui";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useRepQueries } from "../../../api/repApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { useSegmentContext } from "../../../contexts/segmentContext";
import { RepStatusHistory } from "../../../models/repStatusHistory";
import style from "./style.module.scss";

const COLUMNS = [
	new Column("Status", "status"),
	new Column("Description", "description"),
	new Column("Reason", "reason"),
	new LocalDateColumn("Created On", "createdOn").dateOnly(),
	new Column("Created By", "createdBy"),
] as Column<RepStatusHistory>[];

const subColumns = [
	new Column("State").withFormat((i) => i.representativeSegment.stateCode),
	new Column("Status", "status"),
	new LocalDateColumn("Created On", "createdOn").dateOnly(),
	new Column("Created By", "createdBy"),
] as Column<RepStatusHistory>[];

export function StatusHistoryGrid() {
	const { useStatusHistoryQuery, useSegmentStatusHistoryQuery } = useRepQueries();
	const { repId } = useRepContext();
	const { useRepSegmentsQuery } = useRepSegmentsApi(repId);
	const { data: repSegments } = useRepSegmentsQuery();
	const repStatusHistoryQuery = useStatusHistoryQuery();
	const [expanded, setExpanded] = useState<Set<number>>(new Set());
	const { segmentId } = useSegmentContext();
	const repSegmentStatusHistoryQuery = useSegmentStatusHistoryQuery(segmentId);
	const allLoaded =
		(!segmentId && repStatusHistoryQuery.loaded) || (segmentId && repSegmentStatusHistoryQuery.loaded);

	async function clearQuery() {
		await repStatusHistoryQuery.clearQuery();
		await repSegmentStatusHistoryQuery.clearQuery();
	}

	const filteredData = useMemo(() => {
		if (!repStatusHistoryQuery.data?.length) return repStatusHistoryQuery.data;
		if (!segmentId) return repStatusHistoryQuery.data;

		return repSegmentStatusHistoryQuery.data ?? [];
	}, [repStatusHistoryQuery.data, repSegmentStatusHistoryQuery.data, segmentId]);

	const histories = useMemo(() => new KeyedArray(filteredData, (i) => i.id), [filteredData]);

	const hasChildren = useCallback(
		(id: number) => {
			const history = histories.get(id);
			return history?.children?.length > 0;
		},
		[histories],
	);

	const columns = useMemo(() => {
		if (segmentId) return COLUMNS;
		if (repSegments?.length <= 1) return COLUMNS;

		const expanderColumn = new Column<RepStatusHistory>("", "id").withFormat((id) => {
			if (!id || !hasChildren(id)) return null;
			const isExpanded = expanded.has(id);
			return (
				<Button
					onClick={() => handleOpen(id)}
					types={[ButtonTypes.Text]}
					title={isExpanded ? "Collapse" : "Expand"}
				>
					{isExpanded ? (
						<ChevronDown className={style.chevronIcon} />
					) : (
						<ChevronRight className={style.chevronIcon} />
					)}
				</Button>
			);
		});
		const stateColumn = new Column<RepStatusHistory>("State(s)").withFormat((rsh: RepStatusHistory) => {
			if (rsh.representativeSegment?.code) return rsh.representativeSegment?.code;
			if (rsh.children.length === repSegments.length) return "All";
			const states = rsh.children.map((c) => c.representativeSegment.code);
			return states.join(", ");
		});
		return [!segmentId && expanderColumn, stateColumn, ...COLUMNS].filter(Boolean);
	}, [repSegments, expanded, hasChildren, segmentId]);

	const handleOpen = (id: number) => {
		setExpanded((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(id)) {
				newSet.delete(id);
			} else {
				newSet.add(id);
			}
			return newSet;
		});
	};

	const expandedDisplay = (id: number) => {
		if (!expanded.has(id)) return null;

		const history = histories.get(id);
		let children = history?.children;
		if (!children) return null;

		return (
			<GridPreLoaded
				id={`statusHistorySubGrid_${id}`}
				columns={subColumns}
				keyProperty="id"
				values={children}
				hidePagination
			/>
		);
	};

	return (
		<GridPreLoaded
			id="statusHistoryGrid"
			columns={columns}
			keyProperty="id"
			values={histories.data ?? []}
			loading={!allLoaded}
			getData={clearQuery}
			expandedDisplay={expandedDisplay}
		/>
	);
}
